import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button, Container, Form, Table, Card, Row, Col, Modal, ListGroup } from 'react-bootstrap';
import AgreementNameField from '../components/AgreementNameField';
import MissingSelectOption from '../components/MissingSelectOption';
import { cleanupModalBody } from '../utils/modalCleanup';

function emptyForm() {
  return { name: '', customer_name: '', customer_account: '', site_name: '' };
}

function RecordModal({ show, record, onClose, onSave, saving }) {
  const [formData, setFormData] = useState(emptyForm());
  const [modalError, setModalError] = useState('');
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerMatches, setShowCustomerMatches] = useState(false);
  const [sites, setSites] = useState([]);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await axios.get('/api/customers', { params: { page: 1, limit: 10000 } });
        setCustomers(response.data.customers || []);
      } catch {
        setCustomers([]);
      }
    };
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (!show) {
      return;
    }
    const nextForm = record
      ? {
          name: record.name ?? '',
          customer_name: record.customer_name ?? '',
          customer_account: record.customer_account ?? '',
          site_name: record.site_name ?? '',
        }
      : emptyForm();
    setFormData(nextForm);
    setCustomerSearch(nextForm.customer_name);
    setModalError('');
    setShowCustomerMatches(false);
  }, [record, show]);

  useEffect(() => {
    const account = formData.customer_account?.trim();
    if (!account) {
      setSites([]);
      return undefined;
    }

    let active = true;
    const fetchSites = async () => {
      try {
        const response = await axios.get('/api/customer-sites', {
          params: { customer: account, page: 1, limit: 10000 },
        });
        if (active) {
          setSites(response.data.customerSites || []);
        }
      } catch {
        if (active) {
          setSites([]);
        }
      }
    };

    fetchSites();
    return () => {
      active = false;
    };
  }, [formData.customer_account]);

  const filteredCustomers = useMemo(() => {
    const query = customerSearch.trim().toLowerCase();
    if (!query) {
      return [];
    }
    return customers
      .filter((customer) => {
        const name = (customer.name || '').toLowerCase();
        const account = (customer.account_no || '').toLowerCase();
        return name.includes(query) || account.includes(query);
      })
      .slice(0, 20);
  }, [customers, customerSearch]);

  const handleSelectCustomer = (customer) => {
    setFormData((prev) => ({
      ...prev,
      customer_name: customer.name || '',
      customer_account: customer.account_no || '',
      site_name: '',
    }));
    setCustomerSearch(customer.name || '');
    setShowCustomerMatches(false);
    setModalError('');
  };

  const handleCustomerSearchChange = (value) => {
    setCustomerSearch(value);
    setShowCustomerMatches(true);
    setFormData((prev) => ({
      ...prev,
      customer_name: value,
      customer_account: '',
      site_name: '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name?.trim()) {
      setModalError('Agreement Name is required');
      return;
    }
    if (!formData.customer_name?.trim()) {
      setModalError('Customer Name is required');
      return;
    }

    const matchedCustomer = customers.find(
      (customer) =>
        (formData.customer_account &&
          customer.account_no &&
          customer.account_no === formData.customer_account) ||
        customer.name?.trim().toLowerCase() === formData.customer_name.trim().toLowerCase()
    );

    if (!matchedCustomer) {
      setModalError('Please select a customer from the search results');
      return;
    }

    try {
      await onSave({
        name: formData.name.trim(),
        customer_name: matchedCustomer.name,
        customer_account: matchedCustomer.account_no || null,
        site_name: formData.site_name || null,
      });
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to save record');
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      onExited={cleanupModalBody}
      centered
      enforceFocus={false}
      restoreFocus={false}
      style={{ zIndex: 1060 }}
    >
      <Modal.Header closeButton>
        <Modal.Title>{record ? 'Edit Customer / Site' : 'Add Customer / Site'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {modalError && <div className="alert alert-danger">{modalError}</div>}
          <AgreementNameField
            inputName="name"
            required
            active={show}
            refreshKey={record?.id ?? 'new'}
            value={formData.name}
            onChange={(name) => setFormData((p) => ({ ...p, name }))}
          />

          <Form.Group className="mb-3 position-relative">
            <Form.Label>Customer Name *</Form.Label>
            <Form.Control
              type="search"
              name="customer_name_search"
              placeholder="Search by customer name or account..."
              value={customerSearch}
              onChange={(e) => handleCustomerSearchChange(e.target.value)}
              onFocus={() => setShowCustomerMatches(true)}
              onBlur={() => {
                window.setTimeout(() => setShowCustomerMatches(false), 150);
              }}
              autoComplete="off"
              required
            />
            {showCustomerMatches && customerSearch.trim() && filteredCustomers.length > 0 && (
              <ListGroup
                className="position-absolute w-100 shadow-sm"
                style={{ zIndex: 1, maxHeight: '220px', overflowY: 'auto' }}
              >
                {filteredCustomers.map((customer) => (
                  <ListGroup.Item
                    key={customer.id || `${customer.account_no}-${customer.name}`}
                    action
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleSelectCustomer(customer)}
                  >
                    <div className="fw-medium">{customer.name}</div>
                    <div className="small text-muted">
                      {customer.account_no ? `Account: ${customer.account_no}` : 'Cash customer'}
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
            {showCustomerMatches && customerSearch.trim() && filteredCustomers.length === 0 && (
              <div className="small text-muted mt-1">No matching customers</div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Customer Account</Form.Label>
            <Form.Control
              name="customer_account"
              value={formData.customer_account || ''}
              readOnly
              placeholder="Selected automatically from customer lookup"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Site Name</Form.Label>
            <Form.Select
              name="site_name"
              value={formData.site_name}
              onChange={(e) => setFormData((p) => ({ ...p, site_name: e.target.value }))}
              disabled={!formData.customer_account}
            >
              <option value="">
                {formData.customer_account
                  ? 'Select site (optional)...'
                  : 'Select a customer first'}
              </option>
              <MissingSelectOption
                value={formData.site_name}
                options={sites}
                getOptionValue={(site) => site.site_name}
              />
              {sites.map((site) => (
                <option key={site.id} value={site.site_name}>
                  {site.site_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function PricingAgreementCustomerSites() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const listRes = await axios.get('/api/pricing-agreement-customer-sites', {
        params: { page: 1, limit: 10000 },
      });
      setRecords(listRes.data.pricingAgreementCustomerSites || []);
    } catch {
      setError('Failed to fetch records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
    cleanupModalBody();
  }, []);

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`/api/pricing-agreement-customer-sites/${editing.id}`, payload);
        setSuccess('Record updated successfully');
      } else {
        await axios.post('/api/pricing-agreement-customer-sites', payload);
        setSuccess('Record added successfully');
      }
      setShowModal(false);
      setEditing(null);
      await fetchRecords();
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAll = async () => {
    if (
      !window.confirm(
        'Are you sure you want to delete ALL customer/site records? This action cannot be undone!'
      )
    ) {
      return;
    }
    if (
      !window.confirm(
        'This will permanently delete all your pricing agreement customer/site records. Are you absolutely sure?'
      )
    ) {
      return;
    }
    try {
      const response = await axios.delete('/api/pricing-agreement-customer-sites');
      setSuccess(`All records deleted successfully (${response.data.deletedCount || 0} records)`);
      setError('');
      await fetchRecords();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete all records');
    }
  };

  return (
    <Container fluid>
      <Row className="mb-2">
        <Col>
          <h1 className="mb-0">Pricing Agreement Customer / Sites</h1>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          <Button
            onClick={() => {
              setEditing(null);
              setShowModal(true);
            }}
          >
            Add Record
          </Button>
          <Button variant="danger" onClick={handleDeleteAll} disabled={records.length === 0}>
            Delete All
          </Button>
        </Col>
      </Row>
      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}
      <Card>
        <div className="card-body p-0">
          {loading ? (
            <div className="p-4">Loading...</div>
          ) : (
            <Table hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>Agreement</th>
                  <th>Customer Name</th>
                  <th>Account</th>
                  <th>Site</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {records.map((row) => (
                  <tr key={row.id}>
                    <td>{row.name}</td>
                    <td>{row.customer_name}</td>
                    <td>{row.customer_account || '—'}</td>
                    <td>{row.site_name || '—'}</td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="me-1"
                        onClick={() => {
                          setEditing(row);
                          setShowModal(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={async () => {
                          if (!window.confirm('Delete this record?')) return;
                          try {
                            await axios.delete(`/api/pricing-agreement-customer-sites/${row.id}`);
                            fetchRecords();
                          } catch (err) {
                            setError(err.response?.data?.error || 'Failed to delete record');
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </Card>
      <RecordModal
        show={showModal}
        record={editing}
        saving={saving}
        onClose={() => {
          if (saving) return;
          setShowModal(false);
          setEditing(null);
          cleanupModalBody();
        }}
        onSave={handleSave}
      />
    </Container>
  );
}

export default PricingAgreementCustomerSites;
